import { useTranslation } from 'react-i18next'
import ActionFollowButton from './ActionFollowButton'
import ActionStatusTag from './ActionStatusTag'
import { formatDate } from '../../../lib/datetime'
import { deleteLabel } from '../../../lib/deleteLabel'
import {
  ACTION_STATUS,
  mainResponsibleOf,
  secondaryResponsiblesOf,
  type ActionFormRow,
} from '../types'

/**
 * ActionsEditor'in dar ekran karsiligi. Desen SubActionCards ile ayni (ayri dosya, ayni tiklama/klavye
 * bariyeri); veri sekli farkli oldugu icin bilesen kendisi paylasilmadi.
 *
 * Kolonlar karta soyle iniyor: Ad baslik, Durum baslik yaninda rozet, Kayit No alt satir (yalniz yetki
 * varsa), Aciklama kendi satirinda, Ana/Yan Sorumlu (yan sorumlu "+" ile ayrilir) ve Termin bilgi satiri,
 * Duzenle/Kaldir eylem alani. Kayitli satir (id dolu) tiklaninca Aksiyon Detay'a gider; kaydedilmemis
 * yeni satir tiklanamaz (masaustundeki onRowClick kosuluyla birebir).
 */
export default function ActionEditorCards({
  value,
  showRecordNumber,
  removable,
  canFollow = false,
  statusNameById,
  onOpen,
  onEdit,
  onRemove,
  onDelete,
}: Readonly<{
  value: ActionFormRow[]
  showRecordNumber: boolean
  removable: boolean
  /** 120 — kaydedilmiş satırlarda takip yıldızı çizilsin mi (kullanıcı koşulu: `useCanFollowActions`). */
  canFollow?: boolean
  statusNameById: Map<number, string>
  onOpen: (id: number) => void
  onEdit: (row: ActionFormRow) => void
  onRemove: (row: ActionFormRow) => void
  /**
   * **Kaydedilmiş görevi SİL** (2026-09-11) — masaüstü tablosundaki çöp kutusunun kart karşılığı.
   * `onRemove`dan (kaydedilmemiş satırı yerel listeden geri alma) ayrıdır; verilmezse çizilmez.
   * Eklenmeseydi dar ekranda silme özelliği tümüyle kaybolurdu.
   */
  onDelete?: (row: ActionFormRow) => void
}>) {
  const { t } = useTranslation()
  return (
    <div className="ui-card-list act-editor-cards">
      {value.map((row) => {
        const clickable = row.id != null
        const removeLabel = deleteLabel(t, 'actions.detail.delete.action', row.name)
        const unsavedRemoveLabel = deleteLabel(t, 'actions.editor.remove', row.name)
        const mainName = mainResponsibleOf(row.assignees)?.fullName ?? ''
        const secondaryNames = secondaryResponsiblesOf(row.assignees)
          .map((p) => p.fullName)
          .join(', ')
        const body = (
          <>
            <div className="ui-card-head">
              <ActionStatusTag
                statusId={row.statusId}
                name={row.statusId ? (statusNameById.get(row.statusId) ?? '') : ''}
              />
              {row.statusId !== ACTION_STATUS.ARCHIVED && (
                <div className="ui-card-actions">
                  {/* Yıldız (120) — yalnız kaydedilmiş satırda (masaüstü tablosuyla aynı koşul). */}
                  {canFollow && row.id != null && (
                    <ActionFollowButton
                      actionId={row.id}
                      name={row.name}
                      followed={row.followed ?? false}
                    />
                  )}
                  <button
                    type="button"
                    className="minutes-remove-btn"
                    aria-label={t('actions.editor.edit')}
                    title={t('actions.editor.edit')}
                    onClick={() => onEdit(row)}
                  >
                    <i className="pi pi-pencil" aria-hidden="true" />
                  </button>
                  {onDelete && !removable && row.id != null && (
                    <button
                      type="button"
                      className="minutes-remove-btn"
                      aria-label={removeLabel}
                      title={removeLabel}
                      onClick={() => onDelete(row)}
                    >
                      <i className="pi pi-trash" aria-hidden="true" />
                    </button>
                  )}
                  {removable && (
                    <button
                      type="button"
                      className="minutes-remove-btn"
                      aria-label={unsavedRemoveLabel}
                      title={unsavedRemoveLabel}
                      onClick={() => onRemove(row)}
                    >
                      <i className="pi pi-trash" aria-hidden="true" />
                    </button>
                  )}
                </div>
              )}
            </div>
            {/* Kart tıklanabilirse başlık GERÇEK bir kontroldür ve kartı kaplar (ui.css →
                `.ui-card--linked`); kart kökü düz <div> kalır. Önceki biçimde kök `role="button"`
                taşıyordu ve düzenleme/silme düğmeleri onun İÇİNDE kalıyordu — ARIA'da geçersiz,
                ekran okuyucu/klavye için güvenilmez (THY PR incelemesi, 15.09.2026). */}
            {clickable ? (
              <button
                type="button"
                className="ui-card-link"
                onClick={() => onOpen(row.id as number)}
              >
                <span className="ui-card-title">{row.name || ''}</span>
              </button>
            ) : (
              <span className="ui-card-title">{row.name || ''}</span>
            )}

            {showRecordNumber && row.recordNumber && (
              <span className="ui-card-sub">{row.recordNumber}</span>
            )}
            {row.description && <div className="ui-card-row act-card-desc">{row.description}</div>}
            {(mainName || secondaryNames) && (
              <div className="ui-card-row">
                {mainName && <span>{mainName}</span>}
                {secondaryNames && <span className="act-card-secondary">+ {secondaryNames}</span>}
              </div>
            )}
            {row.deadline && <div className="ui-card-row">{formatDate(row.deadline)}</div>}
          </>
        )
        // Kart kökünde artık HİÇ erişilebilirlik özniteliği ya da handler yok; tek fark sınıf adıdır
        // (`--linked` yalnız kaplama katmanını konumlandırır). Bu, eski "iki ayrı <div> çiz" gerekçesini
        // de ortadan kaldırır: o ayrım, `clickable ? … : undefined` biçimindeki koşullu role/tabIndex'i
        // statik çözümleyicinin görememesi (SonarQube S6848/S6845) yüzünden vardı. Öznitelik kalmayınca
        // koşul yalnız sınıfa indi ve o kuralların yüzeyi tümüyle kapandı.
        return (
          <div key={row.key} className={clickable ? 'ui-card ui-card--linked' : 'ui-card'}>
            {body}
          </div>
        )
      })}
    </div>
  )
}
